import type { BorderType } from "../../NotificationEditComponents/BorderSelect";
import type { Branding } from "../../../shared/types/commonTypes";
import type { GetNotificationSettingsResponse } from "../../../shared/payloads/notificationSettingsPayloads";
import type { Dispatch, FC, SetStateAction } from "react";

import { Box, Stack, Typography, Slider } from "@mui/material";

import CustomColorPicker from "../../NotificationEditComponents/CustomColorPicker";
import CustomSwitch from "../../CustomSwitch";
import CustomButton from "../../CustomButton";
import IconSelect from "../../NotificationEditComponents/IconSelect";

import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { sliderStyles } from "../../../constants/styles";

import { NotificationDefaultSettings } from "../../../types/notification-default-settings";

interface NotificationDesignTabProps {
  data?: GetNotificationSettingsResponse;
  branding: Branding;
  active: "classic" | "small" | "compact";
  backgroundColor: string | null;
  textColor: string | null;
  borderRadius: BorderType | null;
  backgroundOpacity: number | null;
  showIcon: boolean;
  iconBackgroundRadius: number | null;
  iconBackgroundColor: string | null;
  disableIcon: boolean;
  icon?: string | null;
  iconColor?: string | null;
  defaultIcon?: string;
  setIconColor?: Dispatch<SetStateAction<string | null>>;
  setIcon?: Dispatch<SetStateAction<string | null>>;
  setIconBackgroundColor: Dispatch<SetStateAction<string | null>>;
  setIconBackgroundRadius: Dispatch<SetStateAction<number | null>>;
  setShowIcon: Dispatch<SetStateAction<boolean>>;
  setBackgroundOpracity: Dispatch<SetStateAction<number | null>>;
  setBorderRadius: Dispatch<SetStateAction<BorderType | null>>;
  setBackgroundColor: Dispatch<SetStateAction<string | null>>;
  setActive: Dispatch<SetStateAction<"classic" | "small" | "compact">>;
  setTextColor: Dispatch<SetStateAction<string | null>>;
}

export const NotificationDesignTab: FC<NotificationDesignTabProps> = ({
  active,
  backgroundColor,
  data,
  branding,
  textColor,
  borderRadius,
  backgroundOpacity,
  iconBackgroundRadius,
  showIcon,
  iconBackgroundColor,
  icon,
  iconColor,
  disableIcon,
  defaultIcon,
  setIcon,
  setIconColor,
  setIconBackgroundColor,
  setIconBackgroundRadius,
  setShowIcon,
  setBackgroundOpracity,
  setBorderRadius,
  setBackgroundColor,
  setActive,
  setTextColor,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        height: 730,
        boxSizing: "border-box",
        overflow: "auto",
        overflowX: "hidden",
        margin: "8px 8px 8px 0",
      }}
    >
      <Typography fontSize={14} style={{ marginTop: 2 }} mb={1}>
        {t("campaign.notifications.settings.design.description")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Stack direction='row' gap={1.5} height={30}>
          <Typography
            sx={{
              color: theme.palette.main.secondary.u1,
              fontWeight: "bold",
              fontSize: 20,
            }}
          >
            {t("campaign.branding.sizeAndForm.title")}
          </Typography>
          {(data?.notificationVisualSettings?.activeSize ??
            branding?.activeSize) &&
            (data?.notificationVisualSettings?.activeSize ??
              branding?.activeSize) !== active && (
              <CustomButton
                onClick={() =>
                  setActive(
                    data?.notificationVisualSettings?.activeSize ??
                      branding?.activeSize ??
                      "classic",
                  )
                }
                title='Reset'
                size='small'
                type='secondary'
                style={{
                  width: 100,
                }}
              />
            )}
        </Stack>
        <Stack direction='row'>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => setActive("classic")}
          >
            <div
              style={{
                height: "100px",
                width: "200px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: "90%",
                  height: "90%",
                  transition: "all 0.3s",
                  border: `3px solid ${
                    active === "classic"
                      ? theme.palette.main.primary.o
                      : theme.palette.natural.t3
                  }`,
                  backgroundColor: active === "classic" ? "#FFF9F1" : "#FFFFFF",
                }}
              />
            </div>
            <Typography>
              {t("campaign.branding.sizeAndForm.classic")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => setActive("small")}
          >
            <div
              style={{
                height: "100px",
                width: "200px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: "90%",
                  height: "60%",
                  transition: "all 0.3s",
                  border: `3px solid ${
                    active === "small"
                      ? theme.palette.main.primary.o
                      : theme.palette.natural.t3
                  }`,
                  backgroundColor: active === "small" ? "#FFF9F1" : "#FFFFFF",
                }}
              />
            </div>
            <Typography>{t("campaign.branding.sizeAndForm.small")}</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => setActive("compact")}
          >
            <div
              style={{
                height: "100px",
                width: "200px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: "70%",
                  height: "90%",
                  transition: "all 0.3s",
                  border: `3px solid ${
                    active === "compact"
                      ? theme.palette.main.primary.o
                      : theme.palette.natural.t3
                  }`,
                  backgroundColor: active === "compact" ? "#FFF9F1" : "#FFFFFF",
                }}
              />
            </div>
            <Typography>
              {t("campaign.branding.sizeAndForm.compact")}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          pt: 1,
        }}
      >
        <Typography
          sx={{
            color: theme.palette.main.secondary.u1,
            fontWeight: "bold",
            fontSize: 20,
          }}
        >
          {t("campaign.branding.defaultSettings.title")}
        </Typography>
        <Stack>
          <Stack direction='row' alignItems='center'>
            <Typography
              fontSize={18}
              color={theme.palette.main.primary.o}
              fontWeight='bold'
              width={300}
            >
              {t("campaign.notifications.settings.design.backgroundColor")}
            </Typography>

            <CustomColorPicker
              color={backgroundColor}
              onChange={setBackgroundColor}
              defaultColor={
                data?.notificationVisualSettings?.backgroundColor ||
                branding?.backgroundColor ||
                NotificationDefaultSettings.BackgroundColor
              }
            />
          </Stack>
          <Stack direction='row' alignItems='center'>
            <Typography
              fontSize={18}
              color={theme.palette.main.primary.o}
              fontWeight='bold'
              width={300}
            >
              {t("campaign.notifications.settings.design.textColor")}
            </Typography>

            <CustomColorPicker
              color={textColor}
              onChange={setTextColor}
              defaultColor={
                data?.notificationVisualSettings?.textColor ||
                branding?.textColor ||
                NotificationDefaultSettings.TextColor
              }
            />
          </Stack>
          <Stack
            direction='row'
            sx={{
              alignItems: "center",
            }}
          >
            <Typography
              fontSize={18}
              color={theme.palette.main.primary.o}
              fontWeight='bold'
              width={250}
            >
              {t("campaign.notifications.settings.design.roundedCorners")}
            </Typography>

            <Stack
              direction='row'
              spacing={2}
              alignItems='center'
              pt={1}
              sx={{
                width: 300,
                height: 44,
              }}
            >
              <Slider
                aria-label='Border radius'
                sx={{
                  ...sliderStyles(false, theme),
                  maxWidth: 200,
                  minWidth: 200,
                }}
                min={0}
                step={1}
                max={30}
                defaultValue={
                  data?.notificationVisualSettings?.roundness ??
                  branding?.borderRadius ??
                  borderRadius ??
                  0
                }
                value={
                  borderRadius ??
                  data?.notificationVisualSettings?.roundness ??
                  branding?.borderRadius ??
                  0
                }
                onChange={(_, value) => {
                  setBorderRadius(value as number);
                }}
              />
              {borderRadius !== null &&
                (data?.notificationVisualSettings?.roundness !== null &&
                data?.notificationVisualSettings?.roundness !== undefined
                  ? borderRadius !== data?.notificationVisualSettings?.roundness
                  : borderRadius !== branding?.borderRadius) && (
                  <CustomButton
                    onClick={() => setBorderRadius(null)}
                    title='Reset'
                    size='small'
                    type='secondary'
                    style={{
                      width: 100,
                    }}
                  />
                )}
            </Stack>
          </Stack>
          <Stack
            direction='row'
            sx={{
              alignItems: "center",
            }}
          >
            <Typography
              fontSize={18}
              color={theme.palette.main.primary.o}
              fontWeight='bold'
              width={250}
            >
              {t("campaign.branding.defaultSettings.backgroundOpacity")}
            </Typography>

            <Stack
              direction='row'
              spacing={2}
              alignItems='center'
              pt={1}
              sx={{
                width: 300,
                height: 44,
              }}
            >
              <Slider
                aria-label='Border radius'
                sx={{
                  ...sliderStyles(false, theme),
                  maxWidth: 200,
                  minWidth: 200,
                }}
                min={0}
                max={1}
                step={0.1}
                defaultValue={
                  data?.notificationVisualSettings?.backgroundOpacity ??
                  branding?.backgroundOpacity ??
                  backgroundOpacity ??
                  1
                }
                value={
                  backgroundOpacity ??
                  data?.notificationVisualSettings?.backgroundOpacity ??
                  branding?.backgroundOpacity ??
                  1
                }
                onChange={(_, value) => {
                  setBackgroundOpracity(value as number);
                }}
              />
              {backgroundOpacity !== null &&
                (data?.notificationVisualSettings?.backgroundOpacity !== null &&
                data?.notificationVisualSettings?.backgroundOpacity !==
                  undefined
                  ? backgroundOpacity !==
                    data?.notificationVisualSettings?.backgroundOpacity
                  : backgroundOpacity !== branding?.backgroundOpacity) && (
                  <CustomButton
                    onClick={() => setBackgroundOpracity(null)}
                    title='Reset'
                    size='small'
                    type='secondary'
                    style={{
                      width: 100,
                    }}
                  />
                )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.main.secondary.u1,
              fontWeight: "bold",
              fontSize: 20,
            }}
          >
            {t("campaign.branding.icons.title")}
          </Typography>
          <CustomSwitch
            checked={showIcon}
            onSwitch={() => {
              setShowIcon(prev => !prev);
              setIconBackgroundColor(null);
              setIconBackgroundRadius(null);

              if (setIcon && setIconColor) {
                setIcon(null);
                setIconColor(null);
              }
            }}
          />
        </Box>
        {showIcon && (
          <>
            <Stack direction='row' alignItems='center'>
              <Typography
                fontSize={18}
                color={theme.palette.main.primary.o}
                fontWeight='bold'
                width={300}
              >
                {t(
                  "campaign.notifications.settings.design.iconbackgroundColor",
                )}
              </Typography>

              <CustomColorPicker
                color={iconBackgroundColor}
                onChange={setIconBackgroundColor}
                defaultColor={
                  data?.notificationVisualSettings?.iconBackgroundColor ||
                  branding?.iconBackgroundColor ||
                  NotificationDefaultSettings.IconBackgroundColor
                }
              />
            </Stack>
            <Stack
              direction='row'
              sx={{
                alignItems: "center",
              }}
            >
              <Typography
                fontSize={18}
                color={theme.palette.main.primary.o}
                fontWeight='bold'
                width={250}
              >
                {t("campaign.branding.icons.backgroundRadius")}
              </Typography>

              <Stack
                direction='row'
                spacing={2}
                alignItems='center'
                pt={1}
                sx={{
                  width: 300,
                  height: 44,
                }}
              >
                <Slider
                  aria-label='Border radius'
                  sx={{
                    ...sliderStyles(false, theme),
                    maxWidth: 200,
                    minWidth: 200,
                  }}
                  min={0}
                  max={50}
                  onChange={(_, value) => {
                    setIconBackgroundRadius(value as number);
                  }}
                  defaultValue={
                    data?.notificationVisualSettings?.iconBackgroundRadius ??
                    branding?.iconBackgroundRadius ??
                    iconBackgroundRadius ??
                    1
                  }
                  value={
                    iconBackgroundRadius ??
                    data?.notificationVisualSettings?.iconBackgroundRadius ??
                    branding?.iconBackgroundRadius ??
                    1
                  }
                />
                {iconBackgroundRadius !== null &&
                  (data?.notificationVisualSettings?.iconBackgroundRadius !==
                    null &&
                  data?.notificationVisualSettings?.iconBackgroundRadius !==
                    undefined
                    ? iconBackgroundRadius !==
                      data?.notificationVisualSettings?.iconBackgroundRadius
                    : iconBackgroundRadius !==
                      branding?.iconBackgroundRadius) && (
                    <CustomButton
                      onClick={() => setIconBackgroundRadius(null)}
                      title='Reset'
                      size='small'
                      type='secondary'
                      style={{
                        width: 100,
                      }}
                    />
                  )}
              </Stack>
            </Stack>
            {!disableIcon && (
              <>
                <Stack direction='row' alignItems='center'>
                  <Typography
                    fontSize={18}
                    color={theme.palette.main.primary.o}
                    fontWeight='bold'
                    width={300}
                  >
                    {t("campaign.notifications.settings.design.iconColor")}
                  </Typography>

                  <CustomColorPicker
                    color={iconColor ?? null}
                    onChange={color => {
                      if (setIconColor) {
                        setIconColor(color);
                      }
                    }}
                    defaultColor={
                      data?.notificationVisualSettings?.iconColor ||
                      branding?.iconColor ||
                      NotificationDefaultSettings.IconColor
                    }
                  />
                </Stack>
                <Stack
                  direction='row'
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 18,
                      color: theme.palette.main.primary.o,
                      fontWeight: "bold",
                    }}
                    mt={1}
                    width={280}
                  >
                    {t("campaign.notifications.settings.design.icon")}
                  </Typography>

                  <Stack
                    direction='row'
                    spacing={2}
                    alignItems='center'
                    pt={1}
                    sx={{
                      width: 300,
                      height: 44,
                    }}
                  >
                    <IconSelect
                      selectedIcon={
                        icon ??
                        data?.notificationVisualSettings?.icon ??
                        defaultIcon ??
                        NotificationDefaultSettings.OrdersIcon
                      }
                      onChange={icon => {
                        if (setIcon) {
                          setIcon(icon);
                        }
                      }}
                      defaultIcon={
                        icon ??
                        data?.notificationVisualSettings?.icon ??
                        defaultIcon
                      }
                    />
                    {icon &&
                      (data?.notificationVisualSettings?.icon
                        ? icon !== data?.notificationVisualSettings?.icon
                        : icon !== defaultIcon) && (
                        <CustomButton
                          onClick={() => {
                            if (setIcon) {
                              setIcon(null);
                            }
                          }}
                          title='Reset'
                          size='small'
                          type='secondary'
                          style={{
                            width: 100,
                          }}
                        />
                      )}
                  </Stack>
                </Stack>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
